import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReviewPage() {
  const { requestId, token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/property/reviews/form/${requestId}/${token}`)
      .then(({ data }) => {
        if (data.already_reviewed) setSubmitted(true);
        setFormData(data);
      })
      .catch(() => setError("Ce lien d'avis est invalide ou a expiré."));
  }, [requestId, token]);

  const handleSubmit = async () => {
    if (rating === 0) { toast.error("Veuillez donner une note"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/property/reviews/submit`, {
        request_id: requestId,
        token,
        rating,
        comment,
        client_name: formData?.client_name || "",
        service_type: formData?.service_type || "",
      });
      setSubmitted(true);
      toast.success("Merci pour votre avis !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  if (error) return (
    <div className="min-h-[60vh] bg-[#EDEBE4] flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Lien invalide</h1>
        <p className="text-[#1A1A1A]/60 text-sm">{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-[60vh] bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="review-success">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Merci !</h1>
        <p className="text-[#1A1A1A]/60 text-sm mb-6">Votre avis a bien été enregistré. Il sera visible après validation par notre équipe.</p>
        <button onClick={() => navigate("/property-services")} className="bg-[#1A1A1A] text-white px-6 py-3 text-xs tracking-[0.15em] uppercase">
          Découvrir nos services
        </button>
      </div>
    </div>
  );

  if (!formData) return <div className="min-h-[60vh] bg-[#EDEBE4] flex items-center justify-center"><p className="text-[#1A1A1A]/50">Chargement...</p></div>;

  return (
    <>
      <SEO title="Donner votre avis — ALTURA Property Services" description="Partagez votre expérience avec ALTURA Property Services." />
      <section className="bg-[#1A1A1A] py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-white/40 text-xs tracking-[0.2em] uppercase mb-3">Property Services</p>
          <h1 className="font-heading text-2xl sm:text-3xl text-white tracking-wide" data-testid="review-title">
            Votre avis compte
          </h1>
          <p className="text-white/50 text-sm mt-3">
            Bonjour {formData.client_name}, comment s'est passée votre intervention de {formData.service_label?.toLowerCase()} ?
          </p>
        </div>
      </section>
      <section className="bg-[#EDEBE4] py-10 sm:py-14">
        <div className="max-w-lg mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-[#1A1A1A]/10 p-6 sm:p-8 space-y-6">
            {/* Stars */}
            <div className="text-center">
              <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-3">Votre note</p>
              <div className="flex justify-center gap-1" data-testid="star-rating">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} onClick={() => setRating(s)} onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)}
                    className="p-1 transition-transform hover:scale-110">
                    <Star size={32} weight={(hover || rating) >= s ? "fill" : "regular"}
                      className={(hover || rating) >= s ? "text-[#D4A853]" : "text-[#1A1A1A]/20"} />
                  </button>
                ))}
              </div>
              {rating > 0 && <p className="text-[#1A1A1A]/50 text-xs mt-2">{rating}/5</p>}
            </div>
            {/* Comment */}
            <div>
              <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Votre commentaire (optionnel)</label>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4}
                placeholder="Partagez votre expérience..."
                className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none resize-none"
                data-testid="review-comment" />
            </div>
            <button onClick={handleSubmit} disabled={submitting || rating === 0}
              className={`w-full py-3 text-xs uppercase tracking-[0.15em] transition-colors ${rating > 0 ? "bg-[#1A1A1A] text-white hover:bg-[#333]" : "bg-[#1A1A1A]/20 text-[#1A1A1A]/40 cursor-not-allowed"}`}
              data-testid="submit-review-btn">
              {submitting ? "Envoi..." : "Envoyer mon avis"}
            </button>
          </motion.div>
        </div>
      </section>
    </>
  );
}
